import React from 'react';
import type { Capability } from '@/types';

// ─── Type colours ─────────────────────────────────────────────────────────────

const TYPE_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  skill:        { bg: 'bg-blue-50 dark:bg-blue-900/20',        text: 'text-blue-700 dark:text-blue-300',       dot: 'bg-blue-400' },
  knowledge:    { bg: 'bg-purple-50 dark:bg-purple-900/20',    text: 'text-purple-700 dark:text-purple-300',   dot: 'bg-purple-400' },
  experience:   { bg: 'bg-green-50 dark:bg-green-900/20',      text: 'text-green-700 dark:text-green-300',     dot: 'bg-green-400' },
  tool:         { bg: 'bg-orange-50 dark:bg-orange-900/20',    text: 'text-orange-700 dark:text-orange-300',   dot: 'bg-orange-400' },
  relationship: { bg: 'bg-pink-50 dark:bg-pink-900/20',        text: 'text-pink-700 dark:text-pink-300',       dot: 'bg-pink-400' },
  credential:   { bg: 'bg-yellow-50 dark:bg-yellow-900/20',    text: 'text-yellow-700 dark:text-yellow-300',   dot: 'bg-yellow-400' },
  domain:       { bg: 'bg-teal-50 dark:bg-teal-900/20',        text: 'text-teal-700 dark:text-teal-300',       dot: 'bg-teal-400' },
};

const STATUS_COLORS: Record<string, string> = {
  active:     'bg-green-400',
  growing:    'bg-blue-400',
  dormant:    'bg-yellow-400',
  deprecated: 'bg-red-400',
};

// ─── Highlight helper ─────────────────────────────────────────────────────────

function highlightText(text: string, query?: string): React.ReactNode {
  if (!query?.trim()) {
    return text;
  }
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) {
    return text;
  }
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-accent-100 dark:bg-accent-900/40 text-accent-800 dark:text-accent-200 rounded-sm px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

// ─── CapabilityCard ───────────────────────────────────────────────────────────

interface CapabilityCardProps {
  capability: Capability;
  onClick?: () => void;
  highlight?: string;
  compact?: boolean;
  selected?: boolean;
}

const CapabilityCard: React.FC<CapabilityCardProps> = ({
  capability,
  onClick,
  highlight,
  compact = false,
  selected = false,
}) => {
  const typeColor = TYPE_COLORS[capability.type] ?? {
    bg: 'bg-surface-50 dark:bg-surface-800',
    text: 'text-surface-600 dark:text-surface-400',
    dot: 'bg-surface-400',
  };

  const strengthPercent = Math.min(100, Math.max(0, capability.strength));
  const strengthColor =
    strengthPercent >= 80
      ? 'bg-green-400'
      : strengthPercent >= 50
      ? 'bg-accent-400'
      : 'bg-surface-300 dark:bg-surface-600';

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'group w-full text-left rounded-xl border transition-all overflow-hidden',
        'bg-white dark:bg-surface-900',
        selected
          ? 'border-primary-400 dark:border-primary-600 shadow-card-hover ring-2 ring-primary-200 dark:ring-primary-900'
          : 'border-surface-200 dark:border-surface-800 shadow-card hover:shadow-card-hover hover:border-primary-300 dark:hover:border-primary-700',
        compact ? 'p-3' : 'p-4',
      ].join(' ')}
    >
      {/* Header row */}
      <div className="flex items-start gap-2.5">
        {/* Type dot */}
        <span
          className={`mt-0.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${typeColor.dot}`}
          title={capability.type}
        />

        <div className="flex-1 min-w-0">
          {/* Title */}
          <h3 className="text-sm font-semibold text-surface-900 dark:text-white leading-snug line-clamp-2">
            {highlightText(capability.title, highlight)}
          </h3>

          {/* Summary */}
          {!compact && capability.summary && (
            <p className="text-xs text-surface-500 dark:text-surface-400 mt-1 line-clamp-2 leading-relaxed">
              {highlightText(capability.summary, highlight)}
            </p>
          )}
        </div>

        {/* Status dot */}
        <span
          className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${STATUS_COLORS[capability.status] ?? 'bg-surface-300'}`}
          title={capability.status}
        />
      </div>

      {/* Bottom row */}
      {!compact && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {/* Type badge */}
          <span className={`px-1.5 py-0.5 rounded-md text-2xs font-medium ${typeColor.bg} ${typeColor.text}`}>
            {capability.type}
          </span>

          {/* Tags */}
          {capability.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="px-1.5 py-0.5 rounded-md text-2xs bg-surface-100 dark:bg-surface-800 text-surface-500 dark:text-surface-400"
            >
              #{tag}
            </span>
          ))}
          {capability.tags.length > 2 && (
            <span className="text-2xs text-surface-400">+{capability.tags.length - 2}</span>
          )}

          {/* Strength bar */}
          <div className="ml-auto flex items-center gap-1.5">
            <div className="w-12 h-1.5 rounded-full bg-surface-200 dark:bg-surface-700 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${strengthColor}`}
                style={{ width: `${strengthPercent}%` }}
              />
            </div>
            <span className="text-2xs text-surface-400 font-mono">{strengthPercent}</span>
          </div>
        </div>
      )}

      {/* Sync indicator (offline/pending) */}
      {capability.syncStatus === 'pending' && (
        <div className="mt-2 flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
          <span className="text-2xs text-yellow-600 dark:text-yellow-400">Pending sync</span>
        </div>
      )}
    </button>
  );
};

export default CapabilityCard;
